import moment from 'moment';
import _ from 'lodash';
import config from '../../app/config';
import {
    DEFAULT_REQUEST_FIELD,
    DEFAULT_STATE as DEFAULT_BED_REQUEST_STATE,
} from '../ward/forms/csm/bedRequestFormExternal';
import Formatter from '../../util/formatter';
import {IconButton} from '@mui/material';
import {AgyleNamedIcon, isIconNameValid} from '../../components/icons/agyle';
import React from 'react';
import Tooltip from '@mui/material/Tooltip';
import clsx from 'clsx';
import {obsStatuses} from '../ward/forms/observation/obsUtils';
import RestoreIcon from '@mui/icons-material/Restore';
import SnoozeIcon from '@mui/icons-material/Snooze';
import BlockIcon from '@mui/icons-material/Block';
import FemaleIcon from '@mui/icons-material/Female';
import MaleIcon from '@mui/icons-material/Male';
import MenuItem from '@mui/material/MenuItem';
import {getReferralsColor} from '../../controls/agyle/tasks/taskUtil';
import SvgIcon from '@mui/material/SvgIcon';

// ********************************************************************** //
// ********************************************************************** //
// *********************INITIAL STATE******************************* //
// ********************************************************************** //
// ********************************************************************** //

export const INITIAL_CHECKED_STATE = {
    checked: false,
    who: null,
    when: null,
    user: null,
    login: null,
    timestamp: null,
    note: '',
}

export const DEFAULT_DESTINATION = {
    hospitalId: null,
    wardId: null,
    wardName: null,
    bayId: null,
    bayName: null,
    bedId: null,
    bedName: null,
};

export const INITIAL_FORM_STATE = {
    ...DEFAULT_BED_REQUEST_STATE,
    destination: {
        ...DEFAULT_DESTINATION,
    },
}

const INIT_STATISTICS_DATA = {
    capacity: {calculatedValue: 0, statisticsValue: 0, changeIndicator: true, statisticsText: 'next hour'},
    available: {calculatedValue: 0, statisticsValue: 0, changeIndicator: true, statisticsText: 'next hour'},
    outliers: {calculatedValue: 0, statisticsValue: 0, changeIndicator: true, statisticsText: 'next hour'},
    occupied: {
        calculatedValue: 0,
        percentageValue: 0,
        statisticsValue: 0,
        changeIndicator: true,
        statisticsText: 'next hour',
    },
    edd: {calculatedValue: 0, statisticsText: 'For today'},
    cdd: {calculatedValue: 0, statisticsText: 'For today'},
    tci: {calculatedValue: 10, statisticsValue: 10, statisticsText: 'Require a bed'},
    preReg: {calculatedValue: 0, statisticsText: 'For today'},
    covid: {calculatedValue: 0, statisticsText: 'For today'},
};

export const PROTOTYPE_HIGH_DIVISION_SPEC_DICT = [
    {id: 'med', __description: 'Medical'},
    {id: 'surg', __description: 'Surgical'},
    {id: 'ortho', __description: 'Orthopaedics'},
    {id: 'paed', __description: 'Paediatrics'},
    {id: 'obs', __description: 'Obstetrics'},
    {id: 'gyn', __description: 'Gynaecology'},
    {id: 'psych', __description: 'Psychiatry'},
];

export const WIDGETS = [
    {
        id: 'capacity',
        key: 'capacity',
        title: 'Capacity',
        size: 'large',
        calculatedValue: 0,
        statisticsValue: 0,
        statisticsText: 'next hour',
        changeIndicator: true,
        selected: false,
        active: true,
    },
    {
        id: 'available',
        key: 'available',
        title: 'Available',
        size: 'large',
        calculatedValue: 0,
        statisticsValue: 0,
        statisticsText: 'next hour',
        changeIndicator: true,
        selected: false,
        active: true,
    },
    {
        id: 'outliers',
        key: 'outliers',
        title: 'Outliers',
        size: 'large',
        calculatedValue: 0,
        statisticsValue: 0,
        statisticsText: 'next hour',
        changeIndicator: true,
        selected: false,
        active: true,
    },
    {
        id: 'occupied',
        key: 'occupied',
        title: 'Occupied',
        size: 'large',
        calculatedValue: 0,
        percentageValue: 0,
        statisticsValue: 0,
        statisticsText: 'next hour',
        changeIndicator: true,
        selected: false,
        active: true,
    },
    {
        id: 'edd',
        key: 'edd',
        title: 'EDD',
        size: 'large',
        calculatedValue: 0,
        statisticsText: 'For today',
        selected: false,
        active: true,
    },
    {
        id: 'cdd',
        key: 'cdd',
        title: 'CDD',
        size: 'large',
        calculatedValue: 0,
        statisticsText: 'For today',
        selected: false,
        active: true,
    },
    {
        id: 'tci',
        key: 'tci',
        title: 'TCIs',
        size: 'large',
        calculatedValue: 0,
        statisticsText: 'Require a bed',
        statisticsValue: 0,
        selected: false,
        active: true,
    },
    {
        id: 'preReg',
        key: 'preReg',
        title: 'Pre-Reg',
        size: 'large',
        calculatedValue: 0,
        statisticsText: 'For today',
        selected: false,
        active: true,
    },
    {
        id: 'covid',
        key: 'covid',
        title: 'Covid',
        size: 'large',
        calculatedValue: 0,
        statisticsText: 'For today',
        selected: false,
        active: true,
    },
];

export const getInitialBMState = (init) => {
    return ({
        ...INITIAL_FORM_STATE,
        ...init,
    });
};

export const bmColorGuide = {
    green: "#4EAC6F",
    yellow: "#F7A202",
    red: "#E25B61",
}

export const STATUSES = Object.freeze({
    AVAILABLE: 'available',
    READY: 'ready',
    CLEANING: 'cleaning',
    OCCUPIED: 'occupied',
    REQUESTED: 'requested',
    RESERVED: 'reserved',
    OUTLIERS: 'outliers',
    CAPACITY: 'capacity',
    CLOSED: 'closed',
    EDD: 'edd',
    CDD: 'cdd',
    TCI: 'tci',
    PREREG: 'preReg',
    COVID: 'covid',
    PREASSIGNED: 'preAssigned',
});

export const PANEL_TYPES = Object.freeze({
    ACTION: 'action',
    ALL: 'all',
    REQUESTED: 'requested',
    ALLOCATED: 'allocated',
    CANCELLED: 'cancelled',
    READY: 'ready',
    COMPLETED: 'completed',
    FOLLOWUP: 'followUp',
    AVAILABLE: 'available',
});

const timerMinutes = {
    green: config?.modules?.bedManagement?.timerMinutesColor?.green || 30,
    yellow: config?.modules?.bedManagement?.timerMinutesColor?.yellow || 120,
    red: config?.modules?.bedManagement?.timerMinutesColor?.red || 240,
}

const timerCountdownMinutes = {
    green: config?.modules?.bedManagement?.timerCountdownMinutesColor?.green || 240,
    yellow: config?.modules?.bedManagement?.timerCountdownMinutesColor?.yellow || 120,
    red: config?.modules?.bedManagement?.timerCountdownMinutesColor?.red || 60,
}

// ********************************************************************** //
// ********************************************************************** //
// *********************TIMER UTILS********************************* //
// ********************************************************************** //
// ********************************************************************** //

export const calculateDifferenceInMinutes = (time, timeTo) => {
    return timeTo ? moment(time).diff(moment(timeTo), 'minutes') :
    moment().diff(moment(time), 'minutes');
}


const getCountdownTimerColor = (time, countdownTo = null) => {
    const difference = calculateDifferenceInMinutes(time, true,  countdownTo);
    
    if (difference < timerCountdownMinutes.green) {
        return 'green';
    } else if (difference < timerCountdownMinutes.yellow) {
        return 'yellow';
    } else {
        return 'red';
    }
}

export const getBedWaitTimerColor = (time, isCountdown = false, countdownTo = null, timeTo = null) => {
    if (isCountdown) {
        return getCountdownTimerColor(time, countdownTo);
    }

    const differenceInMinutes = Math.abs(calculateDifferenceInMinutes(time, timeTo));

    if (differenceInMinutes < timerMinutes.green) {
        return 'green';
    } else if (differenceInMinutes < timerMinutes.yellow) {
        return 'yellow';
    } else {
        return 'red';
    }
}

const calculateBedWait = (value) => moment().diff(moment(value), 'minutes');

export const getBedWaitValue = (patient) => {
    const requestTime = patient?.singleBedRequest?.requested?.when;
    if (!requestTime) return null;

    const bedWait = calculateBedWait(requestTime);
    return bedWait > 0 ? bedWait : 0;
};

// ********************************************************************** //
// ********************************************************************** //
// *********************PATIENT UTILS****************************** //
// ********************************************************************** //
// ********************************************************************** //

export const getPatientObject = (object) => {
    if (Object.hasOwnProperty('dob', object)) {
        return object;
    } else {
        return object?.patient;
    }
};

export const getGenderIcon = (currentPatientInWard) => {
    switch (currentPatientInWard?.patient?.gender) {
        case 'Female':
            return <FemaleIcon fontSize="small"/>;
        case 'Male':
            return <MaleIcon fontSize="small"/>;
        default:
            return 'No gender recorded';
    }
};

export const getPatientName = (currentPatientInWard) => {
    return `${_.upperCase(currentPatientInWard?.patient?.familyName)}, ${currentPatientInWard?.patient?.firstName}`;
};

export const patientMatchesAgeRestriction = (bed, wardObj, ptObj = null) => {
    const pt = ptObj ? ptObj : bed?.patient;
    const ageRestriction = getWardAgeRestriction(wardObj, bed?.wardId);
    const patientAge = moment().diff(moment(pt?.patient?.dob), 'years');
    let matches = true;

    if (_.isEmpty(ageRestriction)) {
        return matches;
    }

    matches = false;
    _.forEach(ageRestriction, (restriction) => {
        const {from, to} = restriction;
        if (patientAge >= from && patientAge < to) {
            matches = true;
        }
    });

    return matches;
};

export const getRequestedPatients = (beds) => {
    return _.filter(beds, item => !item?.singleBedRequest?.canceled?.checked && !item?.singleBedRequest?.patientReady?.checked && !item?.singleBedRequest?.allocated?.checked && item?.singleBedRequest?.requested?.checked);
};

//OUTLIERS -> PATIENTS THAT DON'T MATCH WARD SPECIALTY
export const patientMatchesWardSpecialty = (bed, wardObj) => {
    let matches = true;
    const bedSpec = getWardSpecialtyRestriction(wardObj, bed?.wardId);
    const patientSpecCode = _.get(bed, 'patient.admission.specialtyCode');
    if (patientSpecCode === null) return matches;
    matches = false;

    _.forEach(bedSpec, ({__code: code}) => {
        if (patientSpecCode === code) {
            matches = true;
        }
    });

    return matches;
};

export const isPatientOutlier = (bed, wardObj) => {
    return !patientMatchesWardSpecialty(bed, wardObj);
};

export const getOutliers = (beds, wardObj) => {
    return _.filter(beds, (bed) => !!bed.patient && isPatientOutlier(bed, wardObj));
};


//  BREACHES - > PATIENTS THAT DON'T MATCH WARD AGE OR GENDER RESTRICTION
export const isPatientBreacher = (bed, wardObj, ptObj = null) => {
    const ptField = ptObj || bed?.patient;
    const isBedGenderSpecific = getBedGenderSpecific(bed);
    const ptMatchesGender = isBedGenderSpecific ? ptField?.patient?.gender.toLowerCase() === bed?.genderSpecific.toLowerCase() : true;
    const mainCondition = !ptMatchesGender || !patientMatchesAgeRestriction(bed, wardObj, ptField);
    return ptObj ?
        mainCondition : (bed.patient && mainCondition);
};

export const getBreachers = (beds, wardObj) => {
    return _.filter(beds, (bed) => isPatientBreacher(bed, wardObj));
};

export const getBedManagementRequestStatus = (ptInWard) => {
    if (!ptInWard) return null;
    const singleBedRequest = ptInWard?.singleBedRequest;
    if(singleBedRequest) {
        const {allocated, canceled} = singleBedRequest;

        let status = 'requested';

        if (canceled?.checked) return 'cancelled';
        if (allocated?.checked) return 'allocated';

        return status;
    }
};

export const ptTypeMap = {
    TCI: 'TCI',
    I: 'Inpatient',
    IP: 'Inpatient',
    E: 'ED',
};

export const getBedManagementPatientType = (ptInWard) => {
    return ptTypeMap?.[ptInWard?.admission?.attendanceType] || "Not Recorded";
}

export const changeBMUser = (state, userObj, dict) => {
    const consultant = _.find(dict, {userName: userObj.userName});
    const tester = Formatter.formatUserFullName(userObj);
    
    return ({
        ...state,
        user: tester,
        who: consultant,
        login: userObj.userName,
        timestamp: moment().toISOString()
    })
}

const handleCheckableObj = ({key, checked, who, when, user, login, timestamp, note}) => {
    return ({
        [key]: {
            checked,
            who,
            when,
            user,
            login,
            timestamp,
            note,
        }
    })
}

export const checkRequestObj = (obj, key, userObj, dict, note, isChecked) => {
    return ({
        ...obj,
        [key]: {
            ...obj[key],
            ...changeBMUser(obj[key], userObj, dict),
            when: moment().toISOString(),
            note: note || "",
            checked: isChecked,
        }
    })
}

export const handleAcknowledge = (obj, key, userObj, dict, note, isChecked) => {
    return ({
        ...changeBMUser(obj[key], userObj, dict),
        when: moment().toISOString(),
        note: note || '',
        checked: isChecked,
    });
};

export const changeBedManagementState = (state, key, value) => {
    const {checked, who, when, user, login, timestamp, note} = value;
    return ({
        ...state,
        ...handleCheckableObj({key, checked, who, when, user, login, timestamp, note}),
    })
}

export const handleCancelBedRequest = (bedRequest, userObject, consultantDict, reason, unCancel = false) => {
    let preparedData = _.cloneDeep(getInitialBMState(bedRequest?.singleBedRequest));
    if (unCancel) {
        preparedData.requestedAcknowledge = DEFAULT_REQUEST_FIELD
        preparedData.canceledAcknowledge = DEFAULT_REQUEST_FIELD
        if (_.isNull(preparedData.otherData)) preparedData.otherData = {};
        preparedData.otherData.undoReason = reason;
    }
    const cancelledData = {
        ...preparedData?.canceled,
        ...changeBMUser(preparedData?.canceled, userObject, consultantDict),
        note: unCancel ? '' : reason,
        checked: !unCancel,
        when: moment().toISOString(),
    };
    return changeBedManagementState(preparedData, 'canceled', cancelledData);
}

export const acknowledgeBedRequest = (bedRequest, key, value, userObject, consultantDict, undoAcknowledge = false) => {
    const acknowledgeKey = `${key}Acknowledge`;
    const preparedData = getInitialBMState(bedRequest?.singleBedRequest);
    const acknowledgeData = {
        ...preparedData[acknowledgeKey],
        ...changeBMUser(value, userObject, consultantDict),
        checked: undoAcknowledge ? false : value?.checked,
        when: moment().toISOString(),
    };
    
    return changeBedManagementState(preparedData, acknowledgeKey, acknowledgeData);
}

export const allocateBedRequest = (bedRequest, note, userObject, consultantDict, bedData, isManual, undoAllocate = false) => {
    const reqClone = _.cloneDeep(bedRequest);
    const defaultDestination = {
        hospitalId: null,
        wardId: null,
        wardName: null,
        bayId: null,
        bayName: null,
        bedId: null,
        bedName: null,
    };
    
    let toSave = {
        ...reqClone,
        ...checkRequestObj(reqClone, 'allocated', userObject, consultantDict, note, !undoAllocate),
        destination: undoAllocate ? defaultDestination : reqClone.destination,
    };

    // Auto ack on allocated
    if (!reqClone?.requestedAcknowledge?.checked) {
        toSave = {
            ...toSave,
            requestedAcknowledge: handleAcknowledge(reqClone, 'requestedAcknowledge', userObject, consultantDict, note, true)
        }
    }

    if (!isManual) {
        const ward = getBedWard(bedData);
        const bay = getBedBay(bedData);

        toSave.destination = {
            hospitalId: bedData?.hospitalId,
            wardName: ward?.description,
            wardId: ward?.id,
            bayName: bay?.description,
            bayId: bay?.id,
            bedName: bedData?.name,
            bedId: bedData?.__id,
        };
    }
    return toSave;
}

// ********************************************************************** //
// ********************************************************************** //
// *********************WARD UTILS********************************* //
// ********************************************************************** //
// ********************************************************************** //

export const getWardAgeRestriction = (ward, selectedWard) => _.get(ward, `ageRestriction.${selectedWard}`);

export const getWardSpecialtyRestriction = (ward, selectedWard) => _.get(ward, `specialtyRestriction.${selectedWard}`);

// ********************************************************************** //
// ********************************************************************** //
// *********************BED UTILS************************************ //
// ********************************************************************** //
// ********************************************************************** //

export const getBedAllocationStatus = (bed, requests) => {
    if (bed?.bedStatus) return _.capitalize(bed?.bedStatus?.status);
    const isAllocated = !!bed?.patient;
    const isRequested = isBedRequested(bed, null, requests) || bed?.booked;
    // const isAllocated = !!bed?.patient;
    // const isRequested = isBedRequested(bed, bed?.patient, requests);
    const isAvailable = !bed?.bedStatus && bed?.available && !bed?.patient && !isRequested;

    return isAllocated ? 'Allocated' :
            isRequested ? 'Requested' :
                isAvailable ? 'Available' :
                'Unavailable';
};

export const isBedAgeRestricted = (ageRestriction) => !_.find(ageRestriction, restr => restr?.__description.toLowerCase() === 'none');

export const getBedGenderSpecific = (bed) => {
    return bed?.genderSpecific !== 'unspecified';
}

export const isBedValidForPatientGender = (bed, patient) => {
    return bed.genderSpecific.toLowerCase() === patient?.gender?.toLowerCase();
};

export const isBedReady = (bed) => bed.available && !bed?.bedStatus && !bed?.patient;

export const isBedRequested = (bed, request, requests) => {
    const bedId = bed?.name;
    const bayId = bed?.bay?.id;
    const wardId = bed?.ward?.id;
    let isRequested = false;

    if (request) {
        if (request.singleBedRequest?.destination?.bedId === bedId && request.singleBedRequest?.destination?.bayId === bayId && request.singleBedRequest?.destination?.wardId === wardId) {
            isRequested = true;
        }
    }

    if (!_.isEmpty(requests)) {
        _.forEach(requests, req => {
            if (req.singleBedRequest?.destination?.bedId === bedId && req.singleBedRequest?.destination?.bayId === bayId && req.singleBedRequest?.destination?.wardId === wardId) {
                isRequested = true;
            }
        });
    }
    return isRequested;
};

export const getBedBay = (bed) => bed?.bay;

export const getBedWard  = (bed) => bed?.ward;

export const parseBayInfo = (bay) => {
    if (!bay) return null;
    
    return ({
        id: bay?.__id,
        description: bay?.name,
        type: bay?.type,
        linkedBeds: bay?.linkedBeds,
        flags: bay?.flags,
        wardId: bay?.wardId,
    })
}

export const parseWardInfo = (ward) => {
    if (!ward) return null;
    
    return ({
        id: ward?.id,
        description: ward?.name,
    })
}

export const parseAreaInfoToBed = (bed, parsedBays, parsedWards) => {
    const bay = _.find(parsedBays, bay => bay?.linkedBeds?.includes(bed?.__id));
    const ward = _.find(parsedWards, {id: bed?.wardId});
    
    const toReturn = {
        ...bed,
        bay: parseBayInfo(bay),
        ward: parseWardInfo(ward),
    }
    return toReturn;
}

export const mapBeds = (parsedBeds, parsedBays, parsedWards) => {
    if (!_.isEmpty(parsedBeds)) {
        const mappedBeds = _.map(parsedBeds, bed => {
            return parseAreaInfoToBed(bed, parsedBays, parsedWards);
        });
        
        return mappedBeds;
    } else {
        return [];
    }
}

const getIconName = (opt) => {
    switch (opt) {
        case 'electricity':
        case 'respirator':
            return opt;
        case 'oxygen':
            return 'o2';
        default:
            return '';
    }
};

export const getBedEquipmentIcons = (bed, showEmpty = false) => {
    if (_.isEmpty(bed?.bedOptions)) return showEmpty ? 'None' : null;

    return bed?.bedOptions.map((option) => {
        const iconName = getIconName(option);
        return (
            isIconNameValid(iconName) && (
                <IconButton key={option} style={{maxHeight: 34, maxWidth: 34, paddingBottom: 3}}>
                    <AgyleNamedIcon name={iconName} withTooltip/>
                </IconButton>
            )
        );
    });
};

export const getBedStatuses = (bedId, bedStatuses) => {
    return _.has(bedStatuses, bedId) ? bedStatuses[bedId] : null;
};

export const getPatientBasedOnBedId = (data, bedId) => {
    return _.find(data, patient => (patient?.singleBedRequest?.destination?.bedId === bedId || patient?.bed === bedId)) || null;
};

export const hydrateBeds = (beds, bedStatuses, bmData, wardId) => {
    return beds.map(bed => ({
        ...bed,
        patient: getPatientBasedOnBedId(bmData, bed?.__id),
        bedStatus: getBedStatuses(bed?.__id, bedStatuses?.[wardId] || bedStatuses),
    }));
};

// Available beds are those: no patient assigned, no bedStatus assigned, not archived, available and...
export const getAvailableBedsNotOccupied = (beds) => {
    return _.filter(beds, (bed) => !bed?.archived && bed?.available && !bed?.patient);
};

export const getAvailableBeds = (beds) => {
    return _.filter(beds, (bed) => bed?.status === 'Available');
    // return _.filter(beds, (bed) => !bed?.archived && bed?.available && bed?.bedStatus?.status !== 'closed');
};

export const getUnavailableBeds = (beds) => {
    return _.filter(beds, bed => !!bed?.bedStatus || bed?.patient || !bed?.available || bed?.archived);
};

export const getReadyBeds = (beds) => {
    return _.filter(beds, (bed) => !bed?.archived && bed?.available && !bed?.patient);
};

export const getFilteredByDischargeDate = (beds, type) => {
    const isToday = type?.includes('Today');
    const isCdd = type?.includes('cdd');
    const dateToCheck = isToday ? moment() : moment().add(1, 'day');
    return _.filter(beds, bed => bed?.patientOccupied?.intendentManagement?.[isCdd ? 'cdd' : 'edd']
        && moment(bed?.patientOccupied?.intendentManagement?.[isCdd ? 'cdd' : 'edd']).isSame(dateToCheck, 'day'));
};

export const getCleanedBeds = (beds) => {
    return _.filter(beds, (bed) => bed?.status === 'cleaning' || bed?.status === 'cleaningRequest' || bed?.inCleaning);
    // return _.filter(beds, (bed) => bed?.status === 'Cleaning');
    // return _.filter(beds, (bed) => !bed?.archived && bed?.bedStatus?.status === 'cleaning');
};

export const getClosedBeds = (beds) => {
    return _.filter(beds, (bed) => !bed?.available);
};

// ********************************************************************** //
// ********************************************************************** //
// *********************STATISTICS********************************** //
// ********************************************************************** //
// ********************************************************************** //

export const getWidgetRequestCount = (reqs, mode) => {
    // eslint-disable-next-line default-case
    switch (mode) {
        case 'allAction': {
            const filteredByRequiredAction = _.filter(reqs, (item) => {
                if ((item?.singleBedRequest?.requested?.checked && !item?.singleBedRequest?.requestedAcknowledge?.checked)
                    || (item?.singleBedRequest?.canceled?.checked && !item?.singleBedRequest?.canceledAcknowledge?.checked)) return item;
            });
            return filteredByRequiredAction?.length || 0;
        }
        case 'allRequested': {
            return _.filter(reqs, req => getBedManagementRequestStatus(req) === 'requested').length || 0;
        }
        case 'male': {
            return _.filter(reqs, req => req?.patient?.gender.toLowerCase() === 'male').length || 0;
        }
        case 'female': {
            return _.filter(reqs, req => req?.patient?.gender.toLowerCase() === 'female').length || 0;
        }
        case 'unidentified': {
            return _.filter(reqs, req => (req?.patient?.gender.toLowerCase() !== 'female' && req?.patient?.gender?.toLowerCase() !== 'male')).length || 0;
        }
        case 'sideroom': {
            return _.filter(reqs, req => req?.singleBedRequest?.sideRoom?.checked).length || 0;
        }
        case 'monitored': {
            return _.filter(reqs, req => getBedManagementRequestStatus(req) === 'allocated').length || 0;
        }
    }
};

export const getWidgetCleaningRequestCount = (reqs, mode) => {
    // eslint-disable-next-line default-case
    switch (mode) {
        case 'all':
            return reqs?.length || 0;
        case 'level1':
            return _.filter(reqs, req => req?.level === '1').length || 0;
        case 'level2':
            return _.filter(reqs, req => req?.level === '2').length || 0;
        case 'level3':
            return _.filter(reqs, req => req?.level === '3').length || 0;
        case 'internal':
            return _.filter(reqs, req => req?.managementType === 'internal').length || 0;
        case 'external':
            return _.filter(reqs, req => req?.managementType === 'external').length || 0;
    }
};

export const getWidgetBedCount = (beds, mode) => {
    switch (mode) {
        case 'allRequested': {
            return _.filter(beds, bed => getBedManagementRequestStatus(bed) === 'requested').length;
        }
        case 'male': {
            return _.filter(beds, bed => bed?.patient?.gender.toLowerCase() === 'male').length;
        }
        case 'female': {
            return _.filter(beds, bed => bed?.patient?.gender.toLowerCase() === 'female').length;
        }
    }
};

// ********************************************************************** //
// ********************************************************************** //
// ******************INFECTIONS/ALERTS************************** //
// ********************************************************************** //
// ********************************************************************** //

const isMetadataStatusConfirmed = (field) => ((field?.status === 'confirm' || field?.status === true) && (field?.expired > moment().valueOf()));

export const isCovid = (patient) => isMetadataStatusConfirmed(patient?.metadata?.covid || patient?.metadata?.infections?.covid);

const getInfections = (patientAddData, classes) => {
    const infections = _.filter(patientAddData?.alarms, a => {
        return a.group === 'INFECTION CONTROL' && !a.deleted && moment(a.expireDate).isSameOrAfter(moment());
    });

    return infections.map(infection => {
        const status = infection?.state;
        let iconName = '';
        if (infection?.name === 'COVID 19') {
            switch (status) {
                case 'confirm':
                    iconName = 'covidPositive';
                    break;
                case 'none':
                    iconName = 'covidNegative';
                    break;
                case 'suspected':
                default:
                    iconName = 'covid';
                    break;
            }
        } else {
            const suffix = status === 'suspected' ? 'S' : '';
            iconName = _.toLower(infection.name) + suffix;
        }
        const iconValid = isIconNameValid(iconName);
        return (
            iconValid &&
            <div key={infection.name} className={clsx(classes.singleIconFlag, status, 'padding')}>
                <AgyleNamedIcon name={iconName} withTooltip/>
            </div>
        );
    });
};

const getIce = (currentPatient, classes) => {
    const count = currentPatient?.metadata?.iceReportsCount;
    const iseReset = currentPatient?.metadata?.iceReset?.counter;
    const iceCount = count ? (iseReset ? count - iseReset : count) : 0;

    return (
        (iceCount > 0 || !!iseReset) &&
        <div
            key={'ice'}
            className={clsx(classes.singleIconFlag, 'ice')}
            // onClick={() => props.changeTab('ice')}
        >
            {iceCount > 99 ? 99 : iceCount < 0 ? 0 : iceCount}
        </div>
    );
};

const getDL = (patient, classes) => {
    let status = patient?.studies?.dischargeLetter?.sendStatus === 'OK';

    return (
        patient?.studies?.dischargeLetter?.sendStatus &&
        <div
            key={'dl'}
            className={clsx(classes.singleIconFlag, 'padding')}
            // onClick={openDLS}
        >
            <AgyleNamedIcon
                name={status ? 'dlsSuccess' : 'dlsError'}
                withTooltip
            />
        </div>
    );
};

const getNextObs = (patient, classes) => {
    const nextObsStatus = patient?.patient?.lastObservation?.nextObsStatus;
    if (nextObsStatus) {
        let icon;
        let text;
        // eslint-disable-next-line default-case
        switch (nextObsStatus) {
            case obsStatuses.deferred:
                icon = <RestoreIcon/>;
                text = 'Next observation is deferred';
                break;
            case obsStatuses.suspended:
                icon = <SnoozeIcon/>;
                text = 'Next observation is suspended';
                break;
            case obsStatuses.cancelled:
                icon = <BlockIcon/>;
                text = 'Next observation is cancelled';
                break;
        }

        if (!icon) return;

        return (
            <span>
                    <Tooltip title={text}>
                        <div
                            key="nextObsStatus"
                            className={clsx(classes.singleIconFlag, 'padding')}
                        >
                            {icon}
                        </div>
                    </Tooltip>
             </span>
        );
    }
};

const getDementia = (currentPatientInWard, classes) => {
    const dementiaFlag = _.find(currentPatientInWard?.metadata?.patientFlags, {name: 'dementia'});
    if (dementiaFlag?.checked) {
        return (
            <span className={clsx(classes.statusIconSpec, 'dementia')}>DEMENTIA</span>
        );
    }
};

export const renderAlarmSection = (currentPatientInWard, referredDict, patientAdditionalData, classes) => {
    if (_.isEmpty(referredDict)) return;

    return (
        <>
            {currentPatientInWard.studies?.referrals?.ReferralsToOtherServices &&
                currentPatientInWard.studies.referrals.ReferralsToOtherServices.map((v, index) => {
                    const flag = _.find(referredDict, {snomedCode: v.ReferredToService_SnomedCt.snomedCode});
                    const completed = !_.isEmpty(v?.ReferredToServiceAssessmentTime) && !_.isEmpty(v?.ReferredToServiceAssessmentDate);
                    return (
                        <Tooltip
                            title={flag?.__description}
                            key={flag?.snomedCode}
                        >
                            <div
                                className={clsx(classes.statusIconSpec, completed && 'completed')}
                                style={{
                                    backgroundColor: getReferralsColor(flag),
                                }}
                            >
                                {flag?.notes}
                            </div>
                        </Tooltip>
                    );
                })}
            {getDementia(currentPatientInWard, classes)}
            {getIce(currentPatientInWard, classes)}
            {!!patientAdditionalData && getInfections(patientAdditionalData, classes)}
            {getNextObs(currentPatientInWard, classes)}
            {getDL(currentPatientInWard, classes)}
        </>
    );
};

// ********************************************************************** //
// ********************************************************************** //
// ************************WIDGETS********************************** //
// ********************************************************************** //
// ********************************************************************** //

export const mapWidgetsToAPI = (widgs) => {
    const clone = _.cloneDeep(widgs);
    return clone.filter(wid => wid.active).map((wid) => ({key: wid.key, size: wid.size}));
};

export const mapWidgetsFromAPI = (widgs) => {
    const clone = widgs ? _.cloneDeep(widgs) : [];
    const widgsArr = [];

    for (let wid of clone) {
        const fromInitial = _.find(WIDGETS, {key: wid.key});
        if (fromInitial) {
            widgsArr.push({
                ...fromInitial,
                size: wid?.size,
            });
        }
    }

    return widgsArr;
};

export const getBeds = (data, mode, tci, wardObj) => {
    switch (mode) {
        case STATUSES.CLEANING:
            return _.filter(data, bed => bed?.bedStatus?.status === 'cleaning');
        case STATUSES.CLOSED:
            return _.filter(data, bed => bed?.available && bed?.bedStatus?.status === 'closed');
        case STATUSES.AVAILABLE:
        case STATUSES.READY:
            return _.filter(data, bed => !bed?.bedStatus && bed?.available && !bed?.archived);
        case STATUSES.OUTLIERS:
            return getOutliers(data, wardObj);
        case STATUSES.OCCUPIED:
            return _.filter(data, bed => !!bed?.patient && bed.available);
        case STATUSES.EDD:
            return _.filter(data, bed => bed?.patient?.singleBedRequest?.edd?.checked);
        case STATUSES.CDD:
            return _.filter(data, bed => bed?.patient?.singleBedRequest?.cdd?.checked);
        case STATUSES.TCI:
            return tci || [];
        case STATUSES.PREREG:
            return _.filter(data, bed => bed?.patient?.singleBedRequest?.preReg?.checked);
        case STATUSES.COVID:
            return _.filter(data, bed => isCovid(bed?.patient));
        case STATUSES.PREASSIGNED:
            return _.filter(data, bed => isCovid(bed?.patient));
        default:
            return data;
    }
};

export const getBedsBasedOnStatus = (dataToFilter, ...keys) => {
    let data = {};

    for (let key of keys) {
        data = {
            ...data,
            [key]: getBeds(dataToFilter, key),
        };
    }

    return data;
};

const getCalculatedValue = (data, mode, tci) => getBeds(data, mode, tci)?.length || 0;

const getStatisticsValue = (data, mode, tci, wardObj) => {
    switch (mode) {
        case STATUSES.CAPACITY:
            return getCalculatedValue(data);
        case STATUSES.AVAILABLE:
        case STATUSES.OUTLIERS:
        case STATUSES.OCCUPIED:
            return getCalculatedValue(data, mode, wardObj);
        case STATUSES.TCI: {
            const onlyTciBr = _.filter(tci, t => !t.singleBedRequest?.requested?.checked);
            return onlyTciBr?.length;
        }

        default:
            return '';
    }
};

const getStatisticsText = (_, mode) => {
    switch (mode) {
        case STATUSES.TCI:
            return 'Require a bed';
        case STATUSES.CAPACITY:
        case STATUSES.AVAILABLE:
        case STATUSES.OUTLIERS:
        case STATUSES.OCCUPIED:
            return 'next hour';
        case STATUSES.EDD:
        case STATUSES.CDD:
        case STATUSES.PREREG:
        case STATUSES.COVID:
            return 'For today';
        default:
            return '';
    }
};

export const getPercentageValue = (first, second) => Math.round((first / second) * 100);

const FNS = {
    calculatedValue: getCalculatedValue,
    percentageValue: getPercentageValue,
    statisticsValue: getStatisticsValue,
    statisticsText: getStatisticsText,
};

export const getWidgetData = (data, tci, wardObj) => {
    if (!_.isEmpty(data)) {
        const cloneWgt = _.cloneDeep(INIT_STATISTICS_DATA);
        return _.forEach(cloneWgt, (wgt, wgtKey) => {
            _.forEach(wgt, (value, key) => {
                if (FNS[key]) {
                    if (key === 'percentageValue') {
                        const calculated = FNS.calculatedValue(data, wgtKey);
                        cloneWgt[wgtKey][key] = FNS[key](calculated, data.length);
                    } else {
                        cloneWgt[wgtKey][key] = FNS[key](data, wgtKey, tci, wardObj);
                    }
                }
            });
        });
    }
};

export const mergeData = (data, widgetDataToMerge) => {
    const cloneData = _.cloneDeep(data);
    const cloneWidgetData = _.cloneDeep(widgetDataToMerge);
    
    data.forEach((widg, index) => {
        cloneData[index] = {
            ...widg,
            ...cloneWidgetData?.[widg.id],
        };
    });

    return cloneData;
};

// ********************************************************************** //
// ********************************************************************** //
// *********************FILTER HANDLERS************************** //
// ********************************************************************** //
// ********************************************************************** //

const patientAlreadyInList = (patient, list) => {
    return _.find(list, {__id: patient?.__id});
};

export const handleAgeRestrictionFilter = (data, filterOptions) => {
    if (_.isEmpty(filterOptions)) return data;
    const patients = [];

    _.filter(data, (patient) => {
        const patientAgeInYears = moment().diff(moment(patient?.patient?.dob), 'years');

        for (let filter of filterOptions) {
            if ((patientAgeInYears >= filter?.from && patientAgeInYears < filter?.to) && !patientAlreadyInList(patient, patients)) {
                patients.push(patient);
            }
        }
    });

    return patients;
};

export const handleSpecialtyFilter = (data, filterOptions) => {
    if (_.isEmpty(filterOptions)) return data;
    const patients = [];

    _.filter(data, (patient) => {
        let allocatedPatientSpecialtyCode = patient?.admission?.specialtyCode;
        if (patient?.specialty?.speciality?.__code) allocatedPatientSpecialtyCode = patient?.specialty?.speciality?.__code;

        for (let filter of filterOptions) {
            if (
              (filter?.mainSpecialty === allocatedPatientSpecialtyCode ||
                  filter?.__code === patient?.singleBedRequest?.specialty?.__code ||
                  filter?.__code === patient?.admission?.specialtyCode ||
                  filter?.__code === patient?.specialty?.speciality?.__code
              ) && !patientAlreadyInList(patient, patients)
            ) {
                patients.push(patient);
            }
        }
    });

    return patients;
};

export const handlePatientCtxFilters = (data, filterObj) => {
    const ageFiltered = handleAgeRestrictionFilter(data, filterObj?.age);
    return handleSpecialtyFilter(ageFiltered, filterObj?.spec);
};

export const handleFilterTimeline = (filter, actions) => {
    switch (filter) {
        case 'all':
        default:
            return actions;
        case 'csm':
            return _.filter(actions, action => !action?.noteType);
        case 'bedRequest':
            return _.filter(actions, action => action?.noteType === 'bedRequest');
    }
}

// ********************************************************************** //
// ********************************************************************** //
// *******************************SORT HANDLERS************************** //
// ********************************************************************** //
// ********************************************************************** //

export const SORT_BY_OPTIONS = [
    {id: 0, __description: 'Name: Ascending', value: 'name', direction: 'asc', default: true, name: 'name'},
    {id: 1, __description: 'Name: Descending', value: 'name', direction: 'desc', default: false, name: 'name'},
    {id: 2, __description: 'Date: Latest', value: 'date', direction: 'desc', default: false, name: 'date'},
    {id: 3, __description: 'Date: Oldest', value: 'date', direction: 'asc', default: false, name: 'date'},
];

export const DescIcon = (props) => (
    <SvgIcon width="24px" height="24px" viewBox="0 0 24 24" fill="white"{...props}>
        <path d="m6 20 4-4H7V4H5v12H2zm5-12h9v2h-9zm0 4h7v2h-7zm0-8h11v2H11zm0 12h5v2h-5z"/>
    </SvgIcon>
);

export const AscIcon = (props) => {
    return (
        <SvgIcon width="24" height="24" viewBox="0 0 24 24" {...props}>
            <path d="M11 9h9v2h-9zm0 4h7v2h-7zm0-8h11v2H11zm0 12h5v2h-5zm-6 3h2V8h3L6 4 2 8h3z"/>
        </SvgIcon>
    );
};

export const renderSelectOption = (option) => {
    const {value, direction, __description: description} = option;
    return (
        <MenuItem key={description} value={value} style={{gap: 10, padding: '2px 10px'}}>
            {direction === 'asc' ? <AscIcon direction={direction}/> : <DescIcon/>}
            {description}
        </MenuItem>
    );
};

export const getSortFilterLine = (classes, val) => {
    const {__description, direction, id, value} = val;
    return (
        <MenuItem key={id} name={val} value={val}
                  className={classes.selectField}
                  style={{gap: 10, padding: '5px 10px'}}>
            {direction === 'asc' ? <AscIcon direction={direction}/> : <DescIcon/>}
            {__description}
        </MenuItem>
    );
};

export const handleSortBedByPatientValues = (field, sortDirection, state) => {
    const clone = _.cloneDeep(state);
    switch (field) {
        case 'name':
            return _.orderBy(clone, [
                (item) => _.get(item, 'patient.patient.familyName'),
                (item) => _.get(item, 'patient.patient.firstName'),
            ], [sortDirection, sortDirection]);
        case 'date':
            return _.orderBy(clone, bed => getBedWaitValue(bed?.patient), [sortDirection]);
        default:
            return clone;
    }
};

// ********************************************************************** //
// ********************************************************************** //
// ****************************TABLE COLUMNS***************************** //
// ********************************************************************** //
// ********************************************************************** //

export const getStickyColumnsCount = (panelType, filterType) => {
    switch (panelType) {
        case PANEL_TYPES.ACTION:
        case PANEL_TYPES.ALL:
        case PANEL_TYPES.ALLOCATED:
        case PANEL_TYPES.CANCELLED:
        case PANEL_TYPES.READY:
        case PANEL_TYPES.COMPLETED:
        case PANEL_TYPES.FOLLOWUP:
            return 6;
        case PANEL_TYPES.REQUESTED:
            return 7;
        default:
            return 5;
    }
};